import * as React from "react";
import { CheckIcon, ChevronsUpDown, PlusIcon, XIcon } from "lucide-react";
import { Check } from "lucide-react";

import {
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  CommandEmpty,
  CommandGroup,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import _ from "lodash";
import { Badge } from "@/components/ui/badge";

const POPOVER_WIDTH = "w-[400px]";

interface RemoteMultiSelectProps<T> {
  accessorFn: (option: T) => {
    id: string;
    name: string;
    iconUrl?: string;
    number?: string;
  };
  onSearch: (
    query: string,
    onStatusChanged: (status: "idle" | "loading" | "error" | "success") => void,
  ) => Promise<T[]>;
  label?: string;
  value: T[];
  onValueChanged: (value: T[] | undefined) => void;
  disabled?: boolean;
  parentClassName?: string;
  className?: string;
  showSelected?: boolean;
  onCreate?: (query: string) => T;
  triggerType?: "keyboard" | "change";
}

export function RemoteMultiSelect<T>({
  accessorFn,
  onSearch,
  label,
  value,
  onValueChanged,
  disabled = false,
  parentClassName,
  className,
  showSelected = false,
  onCreate,
  triggerType = "keyboard",
}: RemoteMultiSelectProps<T>) {
  const [open, setOpen] = React.useState(false);

  const handleSetActive = React.useCallback((option: T[] | undefined) => {
    onValueChanged(option);

    // OPTIONAL: close the combobox upon selection
    // setOpen(false);
  }, []);

  return (
    <div
      className={cn("flex flex-col space-y-2 text-foreground", parentClassName)}
    >
      {label && <div className="text-sm font-medium">{label}</div>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className={cn("justify-between truncate w-10/12", className)}
            disabled={disabled}
          >
            {/* {value
              ? value.length < 3
                ? value.map((v) => accessorFn(v).name).join(", ")
                : `Select more...`
              : "Select..."} */}
            Select...
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent side="bottom" className={cn("p-0", POPOVER_WIDTH)}>
          <Search<T>
            selectedResult={value}
            onSelectResult={handleSetActive}
            onSearch={onSearch}
            accessorFn={accessorFn}
            showSelected={showSelected}
            onCreate={onCreate}
            triggerType={triggerType}
          />
        </PopoverContent>
      </Popover>
      {showSelected && (
        <div className="flex flex-row space-x-1">
          {value.map((v) => (
            <Badge key={accessorFn(v).id}>{accessorFn(v).name}</Badge>
          ))}
        </div>
      )}
    </div>
  );
}

interface SearchProps<T> {
  selectedResult?: T[];
  onSelectResult: (value: T[] | undefined) => void;
  onSearch: RemoteMultiSelectProps<T>["onSearch"];
  accessorFn: RemoteMultiSelectProps<T>["accessorFn"];
  showSelected: RemoteMultiSelectProps<T>["showSelected"];
  onCreate: RemoteMultiSelectProps<T>["onCreate"];
  triggerType: RemoteMultiSelectProps<T>["triggerType"];
}

export function Search<T>({
  selectedResult,
  onSelectResult,
  onSearch,
  accessorFn,
  showSelected,
  onCreate,
  triggerType,
}: SearchProps<T>) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [status, setStatus] = React.useState<
    "idle" | "loading" | "error" | "success"
  >("idle");
  const [data, setData] = React.useState<T[]>([]);

  const handleOnSearch = React.useCallback(
    async (value: string) => {
      const result = await onSearch(value, setStatus);
      setData(result);
    },
    [onSearch],
  );

  const handleOnChange = React.useCallback(
    _.debounce((value: string) => {
      if (triggerType === "change") {
        handleOnSearch(value);
      }
    }, 500),
    [handleOnSearch, triggerType],
  );

  return (
    <Command
      shouldFilter={false}
      className="h-auto rounded-lg border border-b-0 shadow-md"
    >
      <CommandInput
        value={searchQuery}
        onValueChange={(value) => {
          setSearchQuery(value);
          handleOnChange(value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && e.shiftKey && triggerType === "keyboard") {
            e.preventDefault();
            handleOnSearch(searchQuery);
          }
        }}
        placeholder="Search..."
      />
      <SearchResults
        query={searchQuery}
        status={status}
        data={data}
        selectedResult={selectedResult}
        onSelectResult={onSelectResult}
        accessorFn={accessorFn}
        onCreate={onCreate}
        triggerType={triggerType}
      />
    </Command>
  );
}

interface SearchResultsProps<T> {
  query: string;
  status: string;
  data: T[];
  selectedResult: SearchProps<T>["selectedResult"];
  onSelectResult: SearchProps<T>["onSelectResult"];
  accessorFn: SearchProps<T>["accessorFn"];
  onCreate: SearchProps<T>["onCreate"];
  triggerType: SearchProps<T>["triggerType"];
}

function SearchResults<T>({
  query,
  selectedResult,
  onSelectResult,
  status,
  data,
  accessorFn,
  onCreate,
  triggerType,
}: SearchResultsProps<T>) {
  const [selectedValues, setSelectedValues] = React.useState<T[]>(
    selectedResult ?? [],
  );

  return (
    <CommandList>
      <CommandGroup>
        {/* TODO: these should have proper loading aria */}
        {status === "pending" && (
          <div className="p-4 text-sm">Searching...</div>
        )}
        {status === "idle" &&
          !data?.length &&
          (query === "" ? (
            <CommandEmpty className="p-2 justify-center">
              Type to search for items
            </CommandEmpty>
          ) : (
            <CommandEmpty className="p-2 justify-center">
              {triggerType === "keyboard" && (
                <span>
                  Press <kbd>Shift + Enter</kbd> to load results
                </span>
              )}
              {triggerType === "change" && (
                <span>Start typing to load results</span>
              )}
            </CommandEmpty>
          ))}
        {status === "success" && !data?.length && (
          <CommandEmpty className="p-2 justify-center flex flex-col">
            No results found
          </CommandEmpty>
        )}
        {status === "error" && (
          <div className="p-4 text-sm">Something went wrong</div>
        )}

        {data?.map((option, index) => {
          const { id, name, iconUrl, number } = accessorFn(option);
          const isSelected =
            _.findIndex(selectedValues, (i) => accessorFn(i).id === id) >= 0;
          return (
            <CommandItem
              key={`${id}-${index}`}
              onSelect={() => {
                const tmpSelectedValues = selectedValues;
                if (isSelected) {
                  _.remove(tmpSelectedValues, (item) =>
                    _.isEqual(item, option),
                  );
                } else {
                  tmpSelectedValues.push(option);
                }
                setSelectedValues(tmpSelectedValues);

                onSelectResult(
                  tmpSelectedValues.length > 0 ? tmpSelectedValues : undefined,
                );
              }}
              value={name}
            >
              <div
                className={cn(
                  "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "opacity-50 [&_svg]:invisible",
                )}
              >
                <CheckIcon className={cn("h-4 w-4")} />
              </div>
              <div className="flex flex-row space-x-4 items-center">
                <div>
                  {iconUrl && (
                    <img className="w-8 h-8 rounded-full" src={iconUrl} />
                  )}
                </div>
                <div>{name}</div>
                {number && (
                  <span className="ml-auto flex-1 h-4 w-4 font-mono text-xs">
                    {number}
                  </span>
                )}
              </div>
            </CommandItem>
          );
        })}
        {query.length > 0 && onCreate && (
          <div className="flex flex-row justify-end">
            <Button
              variant={"ghost"}
              onClick={() => {
                const created = onCreate(query);
                onSelectResult([created]);
              }}
            >
              <PlusIcon />
              Create{" "}
            </Button>
          </div>
        )}
      </CommandGroup>
    </CommandList>
  );
}
